// LiveStreamUserEnteredNotice.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import LiveStreamUserAvatar from '../container/LiveStreamUserAvatar.js';
import UserLevelBadge from '../container/UserLevelBadge.js';
import UserLivestreamBadges from '../container/UserLivestreamBadges.js';
import { USER_BADGE_SIZE } from '../component/UserLivestreamBadges.jsx';
import Trans from '../component/Trans.jsx';
import PAGCanvas from '../component/PAGCanvas.jsx';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

import { textColor, color } from '../style/variables.js';
import media from '../style/media.js';
import SlideInFromLeft from '../style/SlideInFromLeft.js';
import SlideOutToLeft from '../style/SlideOutToLeft.js';

const PAG_CANVAS_MAX_WIDTH = 600;

// The entrance PAG draws the username on a fixed-width text layer, so a long
// name runs into the notice text beside it. Full-width characters take about
// twice the space of half-width ones, so the budget counts width, not length.
// 12 units is roughly 6 full-width or 12 half-width characters.
const PAG_USERNAME_MAX_WIDTH = 12;

const getTruncatedPagUsername = username => {
  let width = 0;
  let endIndex = 0;
  for (const char of username) {
    width += char.codePointAt(0) > 0xff ? 2 : 1;
    if (width > PAG_USERNAME_MAX_WIDTH) {
      return `${username.slice(0, endIndex)}...`;
    }
    endIndex += char.length;
  }
  return username;
};

export class LiveStreamUserEnteredNotice extends React.PureComponent {
  nextTick = null;
  handlePagPlay = () => {
    const { onPagPlay, noticeId } = this.props;
    onPagPlay(noticeId);
  };
  fetchUser = () => {
    const {
      userId,
      username,
      displayName,
      isUserFetching,
      isUserFetched,
      fetchUser,
    } = this.props;
    if (
      userId &&
      (!username || (!displayName && !isUserFetching && !isUserFetched))
    ) {
      this.nextTick = setTimeout(() => {
        fetchUser({ userId });
      });
    }
  };
  componentDidMount() {
    this.fetchUser();
  }
  componentDidUpdate(prevProps) {
    const { userId } = this.props;
    if (userId !== prevProps.userId) {
      this.fetchUser();
    }
  }
  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }
  render() {
    const {
      t,
      noticeStatus,
      streamId,
      userId,
      displayName,
      username,
      level,
      senderBadges,
      isDisplayNameEnabled,
      vipEnteredNoticeStyle,
      entrancePagUrl,
    } = this.props;
    if (!userId) return null;

    const shownUsername = isDisplayNameEnabled
      ? displayName || `@${username}`
      : `@${username}`;

    if (entrancePagUrl) {
      if (!username) return null;

      const pagTextReplacements = [
        {
          index: 0,
          text: t('someone_enter_livestream_v2', {
            ns: TranslationNamespace.LIVESTREAM,
          }),
        },
        { index: 1, text: `${level}` },
        { index: 2, text: getTruncatedPagUsername(shownUsername) },
      ];
      return (
        <StyledLiveStreamUserEnteredNotice status={noticeStatus} $isPag>
          <PagAnimationWrapper>
            <PAGCanvas
              key={`${userId}-${level}`}
              src={entrancePagUrl}
              shouldPlay={
                noticeStatus === 'coming' || noticeStatus === 'display'
              }
              repeat={1}
              onPlay={this.handlePagPlay}
              textReplacements={pagTextReplacements}
            />
          </PagAnimationWrapper>
        </StyledLiveStreamUserEnteredNotice>
      );
    }

    const backgroundColor = vipEnteredNoticeStyle?.['backgroundColor'];
    const borderColor = vipEnteredNoticeStyle?.['borderColor'];

    return (
      <StyledLiveStreamUserEnteredNotice status={noticeStatus}>
        <NoticeBody
          $backgroundColor={backgroundColor}
          $borderColor={borderColor}
        >
          <NoticeBodyBorder $borderColor={borderColor} />
          <NoticeBodyBorderRight $borderColor={borderColor} />
          <NoticeBodyBackground
            $backgroundColor={backgroundColor}
            $borderColor={borderColor}
          />
          <AvatarInfo>
            <AvatarWrapper>
              <LiveStreamUserAvatar
                streamId={streamId}
                userId={userId}
                badgeSize={36}
                avatarSize={30}
                diamondSize={10}
                fontSize={16}
                shouldDisplayMVP={true}
              />
            </AvatarWrapper>
          </AvatarInfo>
          <NoticeInfo>
            <NoticeText>
              <Trans
                defaults={t('chat_vip_watch_livestream', {
                  username: shownUsername,
                  ns: TranslationNamespace.LIVESTREAM,
                }).replace(shownUsername, '<0>{username}</0>')}
                values={{ username: shownUsername }}
                components={[
                  <>
                    <UserLevelBadge level={level} />
                    {shownUsername}
                    <UserLivestreamBadges badges={senderBadges} />
                  </>,
                ]}
              />
            </NoticeText>
          </NoticeInfo>
        </NoticeBody>
      </StyledLiveStreamUserEnteredNotice>
    );
  }
}

LiveStreamUserEnteredNotice.propTypes = {
  t: PropTypes.func.isRequired,
  streamId: PropTypes.string,
  userId: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  isUserFetching: PropTypes.bool,
  isUserFetched: PropTypes.bool,
  isDisplayNameEnabled: PropTypes.bool,
  level: PropTypes.number,
  fetchUser: PropTypes.func,
  noticeStatus: PropTypes.string,
  senderBadges: PropTypes.array,
  vipEnteredNoticeStyle: PropTypes.object,
  entrancePagUrl: PropTypes.string,
  noticeId: PropTypes.string,
  onPagPlay: PropTypes.func,
};

LiveStreamUserEnteredNotice.defaultProps = {
  streamId: '',
  userId: '',
  username: '',
  displayName: '',
  isUserFetching: false,
  isUserFetched: false,
  isDisplayNameEnabled: true,
  level: null,
  fetchUser: () => null,
  noticeStatus: 'display',
  senderBadges: [],
  vipEnteredNoticeStyle: {},
  entrancePagUrl: '',
  noticeId: '',
  onPagPlay: NULL_FUNCTION,
};

const StyledLiveStreamUserEnteredNotice = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  ${({ status, $isPag }) => {
    if ($isPag)
      return css`
        width: 100%;
      `;
    if (status === 'coming') {
      return css`
        animation: 0.5s ${SlideInFromLeft};
      `;
    } else if (status === 'leaving') {
      return css`
        animation: 0.6s ${SlideOutToLeft};
      `;
    }
  }}
`;

const NoticeBody = styled.div`
  position: relative;
  display: flex;
  height: 20px;
  width: auto;
  padding-left: 16px;
  padding-right: 20px;
  margin-left: -16px;
  border-left: none;
  overflow: visible;
`;

/**
 * Handles the top and left borders of the trapezoid shape
 * Uses pseudo-elements to draw borders, supporting both solid colors and gradients
 * Uses background instead of border property because CSS border doesn't support gradients
 */
const NoticeBodyBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -2;
  ${({ $borderColor }) => {
    if ($borderColor) {
      return css`
        /* Top border: extends to top-right corner (100%) to connect with the right diagonal line */
        :before {
          content: '';
          position: absolute;
          top: -1px;
          left: 0;
          width: calc(100% + 0.6px);
          height: 1.5px;
          background: ${$borderColor};
        }
        /* Left border: drawn vertically from top to bottom */
        :after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 1.5px;
          height: 100%;
          background: ${$borderColor};
        }
      `;
    }
  }}
`;

const NoticeBodyBorderRight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -2;
  ${({ $borderColor }) => {
    if ($borderColor) {
      return css`
        /* Bottom border: stops at the start of the right diagonal corner to avoid overlapping with the diagonal line */
        :before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: calc(100% - 19px);
          height: 1.5px;
          background: ${$borderColor};
        }
        /* Creates diagonal effect by rotating 45 degrees
         * Height uses 1.41x (approximately sqrt(2)) to cover the diagonal distance
         * right: -1px and translateY(0.75px) are used to fine-tune position, ensuring perfect connection with top and bottom borders
         */
        :after {
          content: '';
          position: absolute;
          top: 0;
          right: -1px;
          width: 1.5px;
          height: calc(100% * 1.41);
          background: ${$borderColor};
          transform-origin: top right;
          transform: rotate(45deg) translateY(0.75px);
        }
      `;
    }
  }}
`;

/**
 * Creates trapezoid shape using clip-path, with left side vertical (right angle) and right side slanted (offset 20px from top-right to bottom-right corner)
 * clip-path is only applied to the background layer and won't clip child elements
 */
const NoticeBodyBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  /* Trapezoid shape: top-left corner (0%, 0%), top-right corner (100%, 0%), bottom-right corner offset (calc(100% - 20px), 100%), bottom-left corner (0%, 100%) */
  clip-path: polygon(0% 0%, 100% 0%, calc(100% - 20px) 100%, 0% 100%);
  ${({ $backgroundColor }) => {
    if ($backgroundColor) {
      return css`
        background: ${$backgroundColor};
      `;
    } else {
      return css`
        background: linear-gradient(
          270deg,
          ${color.neutral['0_alpha_0']} 6.5%,
          ${color.neutral['0_alpha_40']} 100%
        );
      `;
    }
  }}
`;

const PagAnimationWrapper = styled.div`
  position: relative;
  width: clamp(360px, 40vw, ${PAG_CANVAS_MAX_WIDTH}px);

  ${media.mobile`
    width: 100%;
  `}
`;

const AvatarInfo = styled.div`
  position: relative;
  z-index: 1;
  width: 38px;
`;
const AvatarWrapper = styled.div`
  position: absolute;
  top: -9px;
  left: 0;
`;
const NoticeInfo = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NoticeText = styled.span`
  color: ${textColor.white};
  font-size: 12px;
  line-height: ${USER_BADGE_SIZE}px;
  text-decoration: none;
  display: flex;
  gap: 4px;
`;

export default withTranslation()(LiveStreamUserEnteredNotice);
